'use client';

import { ReactNode, useRef, useState } from 'react';

// Components
import { MlModal } from '@/components/molecules';

// Types
import type { ILocationData } from '@/components/helpers/fetchInitialProps';
import type { ICustomUrls } from '@/lib/contentstack-sdk/types';
import type { IStudioHit } from '@/lib/search/types';
import type { IModalRefProps } from '@/components/molecules/ml-modal/ml-modal.types';
import { EModalVariants } from '@/components/molecules/ml-modal/ml-modal.types';

// Providers
import { AuthProvider } from '@/context/authContext';
import { GlobalBannerProvider } from '@/context/globalBannerContext';
import { GlobalLinksProvider } from '@/context/globalLinksContext';
import { LeadGenModalProvider } from '@/context/leadGenModalContext';
import { LocationProvider } from '@/context/locationContext';
import { LoginModalProvider } from '@/context/loginModalContext';
import { SetLocationModalProvider } from '@/context/setLocationContext';
import TrackingCookieHandler from '@/components/helpers/TrackingCookieHandler';
import { GridScrollRestoreProvider } from '@/context/gridScrollRestoreContext';
import { ContentstackProvider } from '@/lib/contentstack-sdk/contentstack-provider';

interface IProviderProps {
  children: ReactNode;
  locationData: ILocationData[];
  studioData: IStudioHit[];
  globalLinks: ICustomUrls[];
}

export default function Providers({
  children,
  locationData,
  studioData,
  globalLinks,
}: IProviderProps) {
  const modalRef = useRef<IModalRefProps | null>(null);
  const [loginUrl, setLoginUrl] = useState<string>('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);

  return (
    <ContentstackProvider>
      <TrackingCookieHandler />
      <LocationProvider locationData={locationData} studioData={studioData}>
        <AuthProvider>
          <LoginModalProvider modalRef={modalRef} setLoginUrl={setLoginUrl}>
            <GlobalLinksProvider globalLinks={globalLinks}>
              <LeadGenModalProvider>
                <SetLocationModalProvider>
                  <GlobalBannerProvider bannerKey="global-promo-banner">
                    <GridScrollRestoreProvider>
                      {children}
                      <MlModal
                        ref={modalRef}
                        variant={EModalVariants.LOG_IN}
                        onOpen={() => setIsLoginModalOpen(true)}
                        onClose={() => setIsLoginModalOpen(false)}
                      >
                        {loginUrl && isLoginModalOpen ? (
                          <iframe
                            title="Schumacher Login Frame"
                            src={loginUrl}
                            className="h-[36rem] w-full"
                            data-testid="login-iframe"
                          />
                        ) : null}
                      </MlModal>
                    </GridScrollRestoreProvider>
                  </GlobalBannerProvider>
                </SetLocationModalProvider>
              </LeadGenModalProvider>
            </GlobalLinksProvider>
          </LoginModalProvider>
        </AuthProvider>
      </LocationProvider>
    </ContentstackProvider>
  );
}
